import React from "react";
import { useMemo } from "react";
import { renderToString } from "react-dom/server";
import { Theme, useMediaQuery, useTheme } from "@mui/material";
import { toCurrency } from "@helpers";
import { DividendsTooltip } from "./DividendsTooltip";
import { buildDividendChartData } from "./helpers";
import type { DividendPerYear } from "@bn/api/apiv1";
import type { Options } from "highcharts";

interface Params {
  dividends?: DividendPerYear[];
  currency?: string;
  height?: number;
}

export const useChartOptions = ({
  dividends,
  currency = "EUR",
  height = 280,
}: Params): Options => {
  const theme = useTheme();
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down("sm"));

  const solidColor = theme.palette.graphVisual["01"].mid;
  const estimateColor = theme.palette.graphVisual["01"].light;

  const { categories, data } = useMemo(
    () => buildDividendChartData(dividends, solidColor, estimateColor),
    [dividends, solidColor, estimateColor]
  );

  return useMemo(() => {
    const formatAmount = (value: number) =>
      toCurrency(value, currency, { maximumFractionDigits: 2 });

    return {
      title: { text: "" },
      credits: { enabled: false },
      legend: { enabled: false },
      chart: {
        type: "column",
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        height,
      },
      xAxis: {
        type: "category",
        categories,
        lineColor: theme.palette.divider,
        tickColor: theme.palette.divider,
        labels: {
          style: {
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.caption.fontSize as string,
            lineHeight: theme.typography.caption.lineHeight as string,
            fontWeight: "500",
          },
        },
      },
      yAxis: {
        title: { text: "" },
        tickAmount: 4,
        gridLineColor: theme.palette.border.secondary,
        labels: {
          formatter: function () {
            return formatAmount(this.value as number);
          },
          style: {
            color: theme.palette.text.secondary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.caption.fontSize as string,
            lineHeight: theme.typography.caption.lineHeight as string,
            fontWeight: "500",
          },
        },
      },
      tooltip: {
        enabled: isMobile,
        useHTML: true,
        padding: 0,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        formatter: function () {
          return renderToString(
            <DividendsTooltip
              theme={theme}
              year={String(this.point.category)}
              amount={formatAmount(this.y ?? 0)}
              color={this.point.color as string}
            />
          );
        },
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 4,
          dataLabels: {
            enabled: !isMobile,
            inside: false,
            align: "center",
            y: -1,
            allowOverlap: true,
            crop: false,
            overflow: "allow",
            style: {
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.overline.fontSize as string,
              fontWeight: "500",
              textOutline: "none",
            },
            formatter: function () {
              return formatAmount(this.y ?? 0);
            },
          },
        },
      },
      series: [
        {
          type: "column",
          name: "dividends",
          data,
          colorByPoint: true,
        },
      ],
    };
  }, [theme, categories, data, currency, height, isMobile]);
};
